import { supabase } from '../lib/supabase';
import { useAuthStore } from './auth';
import { create } from 'zustand';

interface SyncState {
  connected: boolean;
  lastSynced: Date | null;
  syncError: string | null;
  setSyncStatus: (connected: boolean) => void;
  setLastSynced: (date: Date) => void;
  setSyncError: (error: string | null) => void;
}

export const useSyncStore = create<SyncState>((set) => ({
  connected: false,
  lastSynced: null,
  syncError: null,
  setSyncStatus: (connected) => set({ connected }),
  setLastSynced: (date) => set({ lastSynced: date }),
  setSyncError: (error) => set({ syncError: error }),
}));

export const setupRealtimeSync = () => {
  const { user } = useAuthStore.getState();
  const { setSyncStatus, setLastSynced, setSyncError } = useSyncStore.getState();

  if (!user) {
    setSyncError('User not authenticated');
    return;
  }

  try {
    // Subscribe to study notes changes
    const notesSubscription = supabase
      .channel('study_notes_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'study_notes',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          setLastSynced(new Date());
          syncService.syncNotes();
        }
      )
      .subscribe((status) => {
        setSyncStatus(status === 'SUBSCRIBED');
      });

    // Subscribe to study groups changes
    const groupsSubscription = supabase
      .channel('study_groups_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'study_groups',
          filter: `members=cs.{${user.id}}`,
        },
        () => {
          setLastSynced(new Date());
          syncService.syncGroups();
        }
      )
      .subscribe((status) => {
        setSyncStatus(status === 'SUBSCRIBED');
      });

    return () => {
      notesSubscription.unsubscribe();
      groupsSubscription.unsubscribe();
    };
  } catch (error) {
    setSyncError(error instanceof Error ? error.message : 'Failed to setup sync');
    return () => {};
  }
};

export const syncService = {
  async syncNotes() {
    const { user } = useAuthStore.getState();
    if (!user) return;

    const { data, error } = await supabase
      .from('study_notes')
      .select('*')
      .eq('user_id', user.id);

    if (error) throw error;
    return data;
  },

  async syncGroups() {
    const { user } = useAuthStore.getState();
    if (!user) return;

    const { data, error } = await supabase
      .from('study_groups')
      .select('*')
      .contains('members', [user.id]);

    if (error) throw error;
    return data;
  },
};
